/**
 * LEComparisons - Comparisons section (APR, TIP, 5-year)
 *
 * Shows loan comparison metrics per TRID requirements
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { BarChart3, TrendingUp, Percent, Calendar } from 'lucide-react';
import { LESection } from './LESection';
import type { ParsedLoanEstimate } from './types';

interface LEComparisonsProps {
  data: ParsedLoanEstimate;
  className?: string;
}

/**
 * Format currency for display
 */
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
}

/**
 * Format percentage for display
 */
function formatPercent(value: number): string {
  return `${value.toFixed(3)}%`;
}

export const LEComparisons: React.FC<LEComparisonsProps> = ({
  data,
  className
}) => {
  const comp = data.comparisons;

  return (
    <LESection
      title="Comparisons"
      icon={<BarChart3 className="h-4 w-4" />}
      className={className}
    >
      <p className="text-xs text-muted-foreground mb-4">
        Use these measures to compare this loan with other loans.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* In 5 Years */}
        <div className="p-4 bg-muted/30 rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">In 5 Years</span>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Total you will have paid</span>
              <span className="font-semibold tabular-nums">
                {formatCurrency(comp.fiveYearTotal)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Principal paid off</span>
              <span className="font-semibold tabular-nums">
                {formatCurrency(comp.fiveYearPrincipal)}
              </span>
            </div>
          </div>
        </div>

        {/* APR & TIP */}
        <div className="space-y-4">
          {/* APR */}
          <div className="p-4 bg-muted/30 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Percent className="h-4 w-4 text-muted-foreground" />
                <div>
                  <span className="text-sm font-medium">
                    Annual Percentage Rate (APR)
                  </span>
                  <p className="text-xs text-muted-foreground">
                    Your costs over the loan term expressed as a rate
                  </p>
                </div>
              </div>
              <span className="text-lg font-bold tabular-nums text-primary">
                {formatPercent(comp.apr)}
              </span>
            </div>
          </div>

          {/* TIP */}
          <div className="p-4 bg-muted/30 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
                <div>
                  <span className="text-sm font-medium">
                    Total Interest Percentage (TIP)
                  </span>
                  <p className="text-xs text-muted-foreground">
                    Total interest as a percentage of loan amount
                  </p>
                </div>
              </div>
              <span className="text-lg font-bold tabular-nums">
                {formatPercent(comp.tip)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </LESection>
  );
};

export default LEComparisons;
